import { useRef } from 'react';
import type { JSX } from 'react';
import classNames from 'classnames';

import Tooltip from 'components/partials/tooltip/tooltip';
import ActionsDropdown from 'components/partials/actions-dropdown';
import IconButton from 'components/partials/button/icon-button';
import Input from 'components/partials/input/input';

import { useCurrentUser } from 'hooks/api/use-current-user';
import { useTruncationDetection } from 'hooks/use-truncation-detection';

import { renderModal } from 'helpers/modal-render';
import { formatDate } from 'helpers/dates-formatter';

import { useApiDeleteConversation } from '../../hooks/use-api-delete-conversation';
import { useSessionRename } from '../../hooks/use-session-rename';

import Icons from 'assets/icons';

interface Session {
  id: string;
  name: string;
  createdAt: string;
}

interface SessionItemProps {
  session: Session;
  isActive: boolean;
  setSearchParams: (params: Record<string, string>) => void;
  clientId: string | number;
  isReadOnly?: boolean;
}

const SESSION_ITEM_PRIMARY_MIN_HEIGHT_CLASS = 'min-h-11';

const SessionItem = ({
  session,
  isActive,
  setSearchParams,
  clientId,
  isReadOnly,
}: SessionItemProps): JSX.Element => {
  const currentUser = useCurrentUser();
  const oktaUserId = currentUser.data?.oktaUserId as string;

  const { mutateAsync: deleteConversation } = useApiDeleteConversation({
    oktaUserId,
    clientId,
  });

  const {
    isRenaming,
    draftName,
    displayName,
    isSaving,
    inputRef,
    handleStartRename,
    handleDraftNameChange,
    handleSaveRename,
    handleRenameKeyDown,
  } = useSessionRename({
    conversationId: session.id,
    conversationName: session.name,
    oktaUserId,
    clientId,
  });

  const buttonRef = useRef<HTMLButtonElement>(null);
  const { isTruncated, ref: nameRef } = useTruncationDetection<HTMLSpanElement>(
    {
      containerRef: buttonRef,
      deps: [session.name],
    }
  );

  const handleConfirmDelete = async (): Promise<void> => {
    await deleteConversation(session.id).then(() => setSearchParams({}));
  };

  const handleChatClick = (): void => {
    setSearchParams({ conversationId: session.id });
  };

  const handleSaveClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ): void => {
    event.preventDefault();
    event.stopPropagation();
    void handleSaveRename();
  };

  return (
    <div
      className={classNames(
        'flex items-center justify-between gap-2 px-2 py-2 rounded-md hover:bg-gray-100',
        { 'bg-gray-100': isActive }
      )}
    >
      {isRenaming && !isReadOnly ? (
        <>
          <div
            className={classNames(
              'min-w-0 flex-1 flex items-center',
              SESSION_ITEM_PRIMARY_MIN_HEIGHT_CLASS
            )}
          >
            <Input
              ref={inputRef}
              value={draftName}
              onChange={(event) => handleDraftNameChange(event.target.value)}
              onKeyDown={handleRenameKeyDown}
              disabled={isSaving}
              placeholder=""
              aria-label="Session name"
              data-testid="session-rename-input"
              inputCustomClass="h-8 !py-1 !px-2.5 text-sm"
              inputWrapperCustomClass="!m-0 w-full"
            />
          </div>

          <IconButton
            variant="neutral-text"
            size="xs"
            icon={<Icons.Check />}
            aria-label="Save session name"
            onClick={handleSaveClick}
            disabled={isSaving}
            loading={isSaving}
            className="shrink-0 p-0 border-none mr-1"
            data-testid="session-rename-save"
          />
        </>
      ) : (
        <>
          <Tooltip
            content={displayName}
            enabled={isTruncated}
            anchorStrategy="child"
            positionStrategy="fixed"
            position="bottom-start"
          >
            <button
              ref={buttonRef}
              type="button"
              className={classNames(
                'min-w-0 flex-1 text-left bg-transparent border-none cursor-pointer p-0',
                SESSION_ITEM_PRIMARY_MIN_HEIGHT_CLASS
              )}
              onClick={handleChatClick}
            >
              <span ref={nameRef} className="block text-gray-700 truncate">
                {displayName}
              </span>
              <span className="block text-gray-600 text-sm">
                {formatDate(session.createdAt, 'MMM D, YYYY h:mm A')}
              </span>
            </button>
          </Tooltip>

          {!isReadOnly && (
            <ActionsDropdown
              options={[
                {
                  label: 'Rename',
                  startIcon: <Icons.Edit />,
                  onClick: handleStartRename,
                },
                {
                  label: 'Delete chat',
                  startIcon: <Icons.Delete />,
                  onClick: () =>
                    renderModal({
                      headerText: `Are you sure you want to delete this chat?`,
                      headerSubtext: `This chat will be permanently removed from your session history. You won't be able to restore it.`,
                      onConfirmCallback: handleConfirmDelete,
                      onConfirmButtonLabel: 'Delete chat',
                    }),
                },
              ]}
              variant="reserve-text"
              customClass="hover:!bg-gray-200 py-0 !px-1"
              menuPosition="left"
            />
          )}
        </>
      )}
    </div>
  );
};

export default SessionItem;
